import format from 'dateformat';
import {cloneDeep, fromPairs, get, set} from 'lodash';
import {connect} from 'react-redux';
import actionCreators from '../actionCreators';
import {mkPersistedCoverImageSelection} from '../actionCreators/coverImagesUtil';
import {updatePreferences} from '../actionCreators/preferences';
import {findSection, updateSectionValue} from '../ducks/reportSections';
import AnalyticsGateway from '../lib/AnalyticsGateway';
import CoverImages from '../lib/CoverImages';
import PropertyDetailImages from '../lib/PropertyDetailImages';
import {updatePropertyPreferences as storePropertyPreference} from 'api/propertyPreferences';
import CustomiseCoverPage from './CustomiseCoverPage';

const sectionWithDefaults = (state, reportType) => {
  const section = cloneDeep(findSection(state.reportSections, reportType, 'COVER_PAGE'));
  const agentName = [state.userDetails.firstName, state.userDetails.lastName].join(' ');
  const agencyAddress = [state.userDetails.address1, state.userDetails.address2].join(' ');
  const agencyAddressDetails = [state.userDetails.suburb, state.userDetails.state, state.userDetails.postalCode].join(' ');

  if (!get(section, 'sectionData.singleLineAddress')) {
    set(section, 'sectionData.singleLineAddress', state.propertyDetail.singleLineAddress);
  }
  if (!get(section, 'sectionData.preparedOn')) {
    set(section, 'sectionData.preparedOn', format(new Date(), 'dS mmmm yyyy'));
  }
  if (!get(section, 'sectionData.agentName')) {
    set(section, 'sectionData.agentName', agentName);
  }
  if (!get(section, 'sectionData.agency')) {
    set(section, 'sectionData.agency', state.userDetails.tradeName);
  }
  if (!get(section, 'sectionData.mobile')) {
    set(section, 'sectionData.mobile', state.userDetails.workMobileNumber);
  }
  if (!get(section, 'sectionData.landline')) {
    set(section, 'sectionData.landline', state.userDetails.workPhoneNumber);
  }
  if (!get(section, 'sectionData.email')) {
    set(section, 'sectionData.email', state.userDetails.workEmail);
  }
  if (!get(section, 'sectionData.agentPhoto')) {
    set(section, 'sectionData.agentPhoto', state.userDetails.photo);
  }
  if (!get(section, 'sectionData.agencyLogo')) {
    set(section, 'sectionData.agencyLogo', state.userDetails.agencyLogoUri);
  }
  if (!get(section, 'sectionData.agencyAddress')) {
    set(section, 'sectionData.agencyAddress', agencyAddress);
  }
  if (!get(section, 'sectionData.agencyAddress2')) {
    set(section, 'sectionData.agencyAddress2', agencyAddressDetails);
  }
  return section;
};

const mapStateToProps = (state) => {
  const reportType = state.reportType.value;
  const section = sectionWithDefaults(state, reportType);

  const {coverPageImages, propertyDetail} = state;
  const propertyImages = PropertyDetailImages.list(propertyDetail);
  const customPropertyImages = coverPageImages.custom.filter(({type}) => type === 'property').map(({imageUri}) => imageUri);
  const allPropertyImages = [
    ...customPropertyImages,
    ...propertyImages,
  ];

  const largeCoverPageImages = [
    ...(coverPageImages.large.filter(({franchise}) => franchise).map(({image}) => image)),
    ...(coverPageImages.custom.filter(({type}) => type === 'lifestyle').map(({imageUri}) => imageUri)),
    ...(coverPageImages.large.filter(({franchise}) => !franchise).map(({image}) => image)),
  ];

  const selectedImage = CoverImages.selectedImage(state);

  const otherImages = [selectedImage].map(({type}) => {
    if (type === 'property') {
      return allPropertyImages;
    } else {
      return largeCoverPageImages;
    }
  }).map(images => images.filter(imageUri => imageUri !== selectedImage.src))[0];

  return {
    allPropertyImages,
    largeCoverPageImages,
    otherImages,
    propertyId: state.propertyDetail.propertyId,
    propertyImages,
    reportType,
    section,
    selectedImage : {
      half: selectedImage.type === 'property' ? selectedImage.src : allPropertyImages[0],
      large: selectedImage.type === 'lifestyle' ? selectedImage.src : largeCoverPageImages[0],
    },
    franchiseThumbnails: coverPageImages.large.filter(({franchise}) => franchise).map(({thumbnail}) => thumbnail),
    selectedLayout: selectedImage.type === 'property' ? 'half' : 'large',
    colour: state.theme.colour,
    featureFlags: state.featureFlags,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    sendEvent: (section, value, dataKeyPath) => {
      const labels = {
        singleLineAddress: 'Vanity Address',
      };
      dispatch(actionCreators.sendEditTitleAnalyticsEvent(section, value, labels[dataKeyPath]));
    },
    updateValue: (value, dataKeyPath, reportType) => {
      dispatch(updateSectionValue({
        dataKeyPath: `sectionData.${dataKeyPath}`,
        key: 'COVER_PAGE',
        reportType,
        value,
      }));
    },
    updatePreferences: (preferences, reportType) => {
      dispatch(updatePreferences(preferences, reportType));
    },
    onSelectImage: (layout, imageUri, reportType, propertyId) => {
      mkPersistedCoverImageSelection({
        dispatch,
        imageUri,
        reportType,
        type: layout === 'half' ? 'property' : 'lifestyle',
        propertyId,
      });
    },
  };
};

const mergeProps = (stateProps, dispatchProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    updateAddressPreference: (value, dataKeyPath) => {
      dispatchProps.updateValue(value, dataKeyPath, stateProps.reportType);

      if (dataKeyPath.match(/_isEnabled/i)) {
        let obj = {};
        obj[`coverPage.sectionData.${dataKeyPath}`] = value;
        dispatchProps.updatePreferences(obj, stateProps.reportType);

      } else {
        dispatchProps.sendEvent(stateProps.section, value, dataKeyPath);

        const keyPath = `cover_page.sectionData.${dataKeyPath}`;
        const preference = fromPairs([[keyPath, value]]);
        storePropertyPreference(stateProps.propertyId, preference);
      }
    },
    updateSectionPreference: (value, dataKeyPath) => {
      dispatchProps.updateValue(value, dataKeyPath, stateProps.reportType);
      if (!dataKeyPath.match(/_isEnabled/i)) {
        dispatchProps.sendEvent(stateProps.section, value, dataKeyPath);
      }

      let obj = {};
      obj[`coverPage.sectionData.${dataKeyPath}`] = value;
      dispatchProps.updatePreferences(obj, stateProps.reportType);
    },
    onSelectImage: (layout, imageUrl) => {
      dispatchProps.onSelectImage(layout, imageUrl, stateProps.reportType, stateProps.propertyId);
      AnalyticsGateway.sendEvent({
        eventName: 'Select Image',
        properties: [
          {label: 'Page', value: 'Customise Report'},
          {label: 'Object Context', value: 'Cover Page'},
          {label: 'Property ID', value: stateProps.propertyId},
          {label: 'Report Type', value: stateProps.reportType},
          {label: 'Selected Image', value: !!imageUrl},
          {label: 'CL Photo Name', value: imageUrl && imageUrl.substring(imageUrl.lastIndexOf('/') + 1)},
          {label: 'Layout', value: layout},
        ]
      });
    },
    onSelectLayout: (layout) => {
      const imageUrl = (() => {
        if (layout === 'half') {
          return stateProps.allPropertyImages[0];
        } else {
          return stateProps.largeCoverPageImages[0];
        }
      })();
      dispatchProps.onSelectImage(layout, imageUrl, stateProps.reportType, stateProps.propertyId);
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CustomiseCoverPage);
